"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Tag, Clock, ArrowRight, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";


declare const wvcClient: any;


function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} data-index={i} className="h-96 w-full animate-pulse rounded-xl bg-white/5" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-white/10 bg-white/5 transition-colors hover:border-[hsl(40,80%,60%)]/30">
          <div className="aspect-video w-full overflow-hidden bg-black/20">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-white/5 text-white/20">
                <span className="text-sm">No Image</span>
              </div>
            )}
          </div>
          <CardHeader>
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-[hsl(40,80%,60%)]">
              {post.categories?.[0]?.name || "Event"}
            </div>
            <CardTitle className="font-serif text-xl text-white group-hover:text-[hsl(40,80%,60%)] transition-colors">
              <Link to={post.link || "#"}>
                <span dangerouslySetInnerHTML={{ __html: post.title }} />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="line-clamp-3 text-sm text-white/70"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter>
            <Link to={post.link || "#"} className="group/link flex items-center text-sm font-medium text-[hsl(40,80%,60%)]">
              Read Article <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/link:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedQueryArgs = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPostId], 
    category__in: categoryIds.length > 0 ? categoryIds : undefined, 
    orderby: "rand", 
  };

  const wp_query = new WP_Query(relatedQueryArgs);

  return (
    <div className="mt-24 border-t border-white/10 pt-16">
      <div className="mb-12 flex items-center justify-between">
        <h3 className="font-serif text-3xl font-bold text-white md:text-4xl">
          Related <span className="text-[hsl(40,80%,60%)]">Stories</span>
        </h3>
        <Link to="/blog">
          <Button variant="outline" className="border-[hsl(40,80%,60%)] text-[hsl(40,80%,60%)] hover:bg-[hsl(40,80%,60%)] hover:text-[hsl(220,20%,10%)]">
            View All News
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}


function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-lg bg-white/10" />
        <div className="mb-12 h-96 w-full animate-pulse rounded-xl bg-white/10" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-white/10" />
          <div className="h-4 w-full animate-pulse rounded bg-white/10" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-white/10" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-white">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold">Error Loading Post</h2>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-white">
        <p>Post not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map((cat) => cat.id) || [];

  return (
    <div className="container mx-auto px-6 py-24 lg:px-16">
      <article className="mx-auto max-w-5xl">
        {}
        <header className="mb-12 text-center">
          <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-sm font-medium text-[hsl(40,80%,60%)]">
            {post.categories?.map((cat, i) => (
              <span key={cat.id} data-index={i} className="flex items-center uppercase tracking-wider">
                {i > 0 && <span className="mr-4 text-white/20">•</span>}
                {cat.name}
              </span>
            ))}
          </div>
          
          <h1 
            className="mb-8 font-serif text-4xl font-bold leading-tight text-white md:text-6xl lg:text-7xl"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />

          <div className="flex flex-wrap items-center justify-center gap-6 text-white/60 md:gap-10">
            <div className="flex items-center gap-2">
              <Avatar className="h-10 w-10 border border-white/10">
                <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
                <AvatarFallback className="bg-[hsl(40,80%,60%)] text-[hsl(220,20%,10%)]">
                  {post.author?.name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide text-white/40">Written By</p>
                <p className="text-sm font-medium text-white">{post.author?.name || "Sabor Team"}</p>
              </div>
            </div>
            
            <Separator orientation="vertical" className="hidden h-10 bg-white/10 md:block" />
            
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-[hsl(40,80%,60%)]">
                <Calendar className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide text-white/40">Published</p>
                <p className="text-sm font-medium text-white">
                  {post.date ? new Date(post.date).toLocaleDateString("en-US", { month: 'long', day: 'numeric', year: 'numeric' }) : "Recently"}
                </p>
              </div>
            </div>

            <Separator orientation="vertical" className="hidden h-10 bg-white/10 md:block" />

            <div className="flex items-center gap-2">
               <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-[hsl(40,80%,60%)]">
                <Clock className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="text-xs uppercase tracking-wide text-white/40">Read Time</p>
                <p className="text-sm font-medium text-white">5 Min Read</p>
              </div>
            </div>
          </div>
        </header>

        {}
        {post.featuredImage && (
          <div className="mb-16 overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/50">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-auto w-full object-cover"
            />
          </div>
        )}

        {}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_300px]">
          <div className="prose prose-lg prose-invert max-w-none">
            <div 
              className="
                text-white/80 
                [&>p]:mb-6 [&>p]:leading-relaxed 
                [&>h2]:mb-6 [&>h2]:mt-12 [&>h2]:font-serif [&>h2]:text-3xl [&>h2]:text-[hsl(40,80%,60%)] 
                [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:font-serif [&>h3]:text-2xl [&>h3]:text-white 
                [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mb-2 
                [&>blockquote]:border-l-4 [&>blockquote]:border-[hsl(40,80%,60%)] [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-white
                [&>a]:text-[hsl(40,80%,60%)] [&>a]:underline [&>a]:decoration-1 [&>a]:underline-offset-4 hover:[&>a]:text-[hsl(160,50%,40%)]
                [&_img]:rounded-xl [&_img]:border [&_img]:border-white/10
              "
              dangerouslySetInnerHTML={{ __html: post.content || "" }} 
            />
            
            {}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-white/10 pt-8">
                <Tag className="h-4 w-4 text-[hsl(40,80%,60%)]" />
                <span className="mr-2 text-sm font-medium text-white">Tags:</span>
                {post.tags.map((tag, i) => (
                  <Link key={tag.id} data-index={i} to={tag.link || "#"}>
                    <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-medium text-white hover:bg-[hsl(40,80%,60%)] hover:text-[hsl(220,20%,10%)] transition-colors">
                      #{tag.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {}
          <aside className="space-y-8">
            {}
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
              <Avatar className="mx-auto mb-4 h-20 w-20 border-2 border-[hsl(40,80%,60%)]">
                <AvatarImage src={post.author?.avatar?.["96"] || ""} />
                <AvatarFallback>{post.author?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <h3 className="mb-1 font-serif text-xl font-bold text-white">{post.author?.name}</h3>
              <p className="mb-4 text-xs uppercase tracking-widest text-[hsl(40,80%,60%)]">Content Creator</p>
              <p className="mb-6 text-sm text-white/70">
                Bringing you the latest updates from Sabor Denver's culinary and cultural experiences.
              </p>
              <div className="flex justify-center gap-4">
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-white/60 hover:bg-[hsl(40,80%,60%)] hover:text-[hsl(220,20%,10%)]">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-white/60 hover:bg-[hsl(40,80%,60%)] hover:text-[hsl(220,20%,10%)]">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-white/60 hover:bg-[hsl(40,80%,60%)] hover:text-[hsl(220,20%,10%)]">
                  <Facebook className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {}
            <div className="rounded-xl border border-white/10 bg-[hsl(220,20%,15%)] p-6">
              <h3 className="mb-4 flex items-center gap-2 font-serif text-lg font-bold text-white">
                <Share2 className="h-4 w-4 text-[hsl(40,80%,60%)]" /> Share this Article
              </h3>
              <div className="flex flex-col gap-3">
                <Button variant="outline" className="w-full justify-start border-white/10 text-white hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]">
                  <Facebook className="mr-2 h-4 w-4" /> Facebook
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-white hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2]">
                  <Twitter className="mr-2 h-4 w-4" /> Twitter
                </Button>
                <Button variant="outline" className="w-full justify-start border-white/10 text-white hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]">
                  <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
                </Button>
              </div>
            </div>
          </aside>
        </div>

        {}
        <RelatedPostsSection currentPostId={post.id} categoryIds={categoryIds} />
      </article>
    </div>
  );
}

export default function SinglePostTemplate() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-[hsl(220,20%,10%)] text-white">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}